import { ExtraUserContractPopupRepository } from "../domain/ExtraUserContractPopupRepository";
import { ExtraUserContractPopupScreenData } from "../domain/ExtraUserContractPopupScreenData";
import { FileRepository } from "../../shared/domain/FileRepository";
import { v4 as uuidv4 } from 'uuid';

export class ExtraUserContractPopupAllCommands {
    screen_data: ExtraUserContractPopupScreenData;
    repo: ExtraUserContractPopupRepository;
    file_repo: FileRepository;

    constructor(screen_data: ExtraUserContractPopupScreenData, repo: ExtraUserContractPopupRepository, file_repo: FileRepository) {
        this.screen_data = screen_data;
        this.repo = repo;
        this.file_repo = file_repo;
    }

    pricePerUser(): number {
        const price_per_user = this.screen_data.selected_contract_for_extra_user_contract_popup.extra_users_price * this.screen_data.selected_contract_for_extra_user_contract_popup.remaining_contract_percentage / 100;
        return Math.round(price_per_user * 100) / 100;
    }

    changeView(view: string) {
        switch (view) {
            case "select_number_of_extra_users":
                this.screen_data.title = "Contratar usuarios adicionales";
                break;
            case "payment_method":
                this.screen_data.title = "Método de pago";
                break;
            case "loading":
                this.screen_data.title = "Cargando...";
                break;
            case "success":
                this.screen_data.title = "Pago exitoso";
                break;
            case "fail":
                this.screen_data.title = "Pago fallido";
                break;
        }
        this.screen_data.view = view;
    }

    next() {
        this.changeView('payment_method');
    }

    async getUserTax() {
        const result = await this.repo.getUserTax({});
        this.screen_data.tax_imposed = result.tax_imposed;
        this.screen_data.tax_name = result.tax_name;
        this.screen_data.tax = result.tax;
    }

    calculateToPay(): string {
        let total_price = 0;
        if (this.screen_data.tax_imposed && this.screen_data.tax_imposed > 0) {
            total_price += this.pricePerUser() * this.screen_data.number_of_extra_users * ((this.screen_data.tax_imposed / 100) + 1);
        } else {
            total_price += this.screen_data.selected_contract_for_extra_user_contract_popup.extra_users_price * this.screen_data.number_of_extra_users;
        }
        return total_price.toFixed(2);
    }

    changeNumberOfExtraUsers() {
        if (this.screen_data.number_of_extra_users <= 0) {
            this.screen_data.number_of_extra_users = 1;
        }
        this.calculateToPay();
    }

    changePaymentMethod(payment_method: string) {
        this.screen_data.payment_method = payment_method;
    }

    changeFile() {
        if (this.screen_data.payment_file instanceof File && this.screen_data.payment_file.type !== 'application/pdf') {
            this.screen_data.payment_file = null;
            this.screen_data.file_input_key++;
            this.screen_data.file_input_error = true;
            return;
        } else {
            this.screen_data.file_input_error = false;
        }
    }

    isDisabledPayWithTransfer(): boolean {
        return !this.screen_data.payment_file;
    }

    async rememberUsedCard() {
        const response = await this.repo.rememberUsedCard({});
        if (!response) {
            return;
        }
        this.screen_data.used_card.stripe_customer_id = response.card_data.stripe_customer_id;
        this.screen_data.used_card.payment_method_id = response.card_data.payment_method_id;
        this.screen_data.used_card.last4 = response.card_data.last4;
        this.screen_data.used_card.use_old_card = response.card_data.use_old_card;
    }

    async payWithTransfer(forceRefreshUserData?: () => Promise<void>) {
        this.changeView("loading");
        let payment_file_path = await this.file_repo.uploadFile(this.screen_data.payment_file);
        let response = await this.repo.purchaseExtraUsers({
            sd_id: this.screen_data.selected_contract_for_extra_user_contract_popup.id,
            charge_id: uuidv4(),
            extra_users: Number(this.screen_data.number_of_extra_users),
            payment_file_path: payment_file_path,
        });

        if (response && response.output) {
            this.screen_data.error_message = response.output.message;
            this.changeView(response.output.success ? 'success' : 'fail');
            if (response.output.success && forceRefreshUserData) {
                await forceRefreshUserData();
            }
        }
    }

    async payWithCard() {
        if (this.screen_data.used_card.use_old_card) {
            await this.payWithOldCard();
        } else {
            await this.payWithNewCard();
        }
    }

    async payWithOldCard() {
        this.changeView("loading");
        const args = {
            service_delivery_id: this.screen_data.selected_contract_for_extra_user_contract_popup.id,
            stripe_customer_id: this.screen_data.used_card.stripe_customer_id,
            payment_method_id: this.screen_data.used_card.payment_method_id,
            extra_users: this.screen_data.number_of_extra_users,
        };
        const response = await this.repo.payWithOldCard(args);
        const view = response.output.status == 'ok' ? 'success' : 'fail';
        this.changeView(view);
    }

    async payWithNewCard() {
        this.changeView("loading");
        const args = {
            stripe_customer_id: this.screen_data.used_card.stripe_customer_id,
            sd_id: this.screen_data.selected_contract_for_extra_user_contract_popup.id,
            extra_users: this.screen_data.number_of_extra_users,
        };
        const response = await this.repo.payWithNewCard(args);
        window.open(response.output.url, '_blank', 'width=388, height=689');
    }

    createListener() {
        this.screen_data.message_listener = (event) => {
            console.log("Received message:", event.data);
            if (event.data.stripe_result) {
                const view = event.data.stripe_result == "ok" ? "success" : "fail";
                this.changeView(view);
            }
        }
        window.addEventListener('message', this.screen_data.message_listener)
    }

    async run(selected_contract_for_extra_user_contract_popup) {
        this.screen_data.selected_contract_for_extra_user_contract_popup = selected_contract_for_extra_user_contract_popup;
        this.createListener();
        await this.getUserTax();
        await this.rememberUsedCard();
        this.changeView("select_number_of_extra_users");
    }
}